import React, { useState, useEffect } from 'react';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { MoneyInput } from '../ui/money-input';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { AlertCircle, ArrowRight, RefreshCw } from 'lucide-react';
import { transactionService } from '../../services/transactionService';
import cashBoxService, { CashBox } from '../../services/cashBoxService';
import { CreateTransferRequest } from '../../types';
import { showError } from '../Alerts';

interface TransferModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

/**
 * Modal para transferencias entre cajas.
 * - Misma moneda: TRANSFERENCIA_INTERNA
 * - Distinta moneda: activa modo conversión con tipo de cambio (1 USD = X ARS)
 *   USD → ARS: recibís = enviás × cotización
 *   ARS → USD: recibís = enviás / cotización
 */
export default function TransferModal({
  isOpen,
  onClose,
  onSuccess,
}: TransferModalProps) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [cashBoxes, setCashBoxes] = useState<CashBox[]>([]);

  const [sourceCashBoxId, setSourceCashBoxId] = useState<number>(0);
  const [targetCashBoxId, setTargetCashBoxId] = useState<number>(0);
  const [amount, setAmount] = useState<number | null>(null);
  const [exchangeRate, setExchangeRate] = useState<number | null>(null);
  const [description, setDescription] = useState('');

  useEffect(() => {
    if (isOpen) {
      cashBoxService.getAllCashBoxes()
        .then((boxes) => setCashBoxes(boxes.filter((b) => b.isActive)))
        .catch(() => setError('Error al cargar las cajas disponibles'));
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) {
      setSourceCashBoxId(0);
      setTargetCashBoxId(0);
      setAmount(null);
      setExchangeRate(null);
      setDescription('');
      setError(null);
    }
  }, [isOpen]);

  const sourceBox = cashBoxes.find((b) => b.id === sourceCashBoxId);
  const targetBox = cashBoxes.find((b) => b.id === targetCashBoxId);
  const isConversion = !!(sourceBox && targetBox && sourceBox.currency !== targetBox.currency);

  // Convención 1 USD = exchangeRate ARS
  const convertedAmount =
    isConversion && exchangeRate && exchangeRate > 0 && amount && amount > 0
      ? sourceBox?.currency === 'USD'
        ? amount * exchangeRate      // USD → ARS: multiplicar
        : amount / exchangeRate      // ARS → USD: dividir
      : null;

  const fmt = (val: number, currency: string) =>
    `${currency} ${val.toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!sourceCashBoxId) { setError('Debe seleccionar una caja de origen'); return; }
    if (!targetCashBoxId) { setError('Debe seleccionar una caja de destino'); return; }
    if (sourceCashBoxId === targetCashBoxId) { setError('Las cajas de origen y destino deben ser diferentes'); return; }
    if (!amount || amount <= 0) { setError('El monto debe ser mayor a cero'); return; }

    if (sourceBox && sourceBox.balance < amount) {
      setError(`Saldo insuficiente. Disponible: ${fmt(sourceBox.balance, sourceBox.currency)}`);
      return;
    }

    if (isConversion && (!exchangeRate || exchangeRate <= 0)) {
      setError('Debe ingresar un tipo de cambio positivo (1 USD = X ARS)');
      return;
    }

    try {
      setLoading(true);
      const payload: CreateTransferRequest = {
        sourceCashBoxId,
        targetCashBoxId,
        amount,
        description,
        exchangeRate: isConversion ? exchangeRate! : undefined,
      };
      await transactionService.createTransfer(payload);
      onSuccess();
      onClose();
    } catch (err: any) {
      const msg = err.response?.data?.message || 'Error al registrar la transferencia. Intente nuevamente.';
      setError(msg);
      showError(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[580px]">
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {isConversion
                ? <><RefreshCw className="w-4 h-4 text-amber-500" /> Conversión entre Cajas</>
                : <><ArrowRight className="w-4 h-4" /> Transferencia entre Cajas</>}
            </DialogTitle>
            <DialogDescription>
              {isConversion
                ? 'Cajas con distinta moneda — se activó el modo conversión. Ingresá el tipo de cambio.'
                : 'Mover dinero entre cajas. Si elegís cajas con distinta moneda se activará conversión.'}
            </DialogDescription>
          </DialogHeader>

          <div className="grid gap-4 py-4">
            {error && (
              <div className="flex items-center gap-2 p-3 text-sm text-red-800 bg-red-100 rounded-md">
                <AlertCircle className="w-4 h-4 flex-shrink-0" />
                <span>{error}</span>
              </div>
            )}

            {/* Caja Origen */}
            <div className="grid gap-1.5">
              <Label>Caja Origen (sale dinero) *</Label>
              <Select
                value={sourceCashBoxId.toString()}
                onValueChange={(v) => {
                  setSourceCashBoxId(parseInt(v));
                  setTargetCashBoxId(
                    targetCashBoxId === parseInt(v) ? 0 : targetCashBoxId
                  );
                  setExchangeRate(null);
                }}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Seleccionar caja origen" />
                </SelectTrigger>
                <SelectContent>
                  {cashBoxes.map((box) => (
                    <SelectItem key={box.id} value={box.id.toString()}>
                      {box.name} — {fmt(box.balance, box.currency)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {sourceBox && (
                <p className="text-xs text-muted-foreground">
                  Saldo disponible: <span className="font-medium">{fmt(sourceBox.balance, sourceBox.currency)}</span>
                </p>
              )}
            </div>

            <div className="flex justify-center">
              {isConversion
                ? <RefreshCw className="w-5 h-5 text-amber-400" />
                : <ArrowRight className="w-5 h-5 text-muted-foreground" />}
            </div>

            {/* Caja Destino */}
            <div className="grid gap-1.5">
              <Label>Caja Destino (entra dinero) *</Label>
              <Select
                value={targetCashBoxId.toString()}
                onValueChange={(v) => { setTargetCashBoxId(parseInt(v)); setExchangeRate(null); }}
                disabled={!sourceCashBoxId}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Seleccionar caja destino" />
                </SelectTrigger>
                <SelectContent>
                  {cashBoxes
                    .filter((b) => b.id !== sourceCashBoxId)
                    .map((box) => (
                      <SelectItem key={box.id} value={box.id.toString()}>
                        {box.name} — {fmt(box.balance, box.currency)}
                        {box.currency !== sourceBox?.currency ? `  [${box.currency}]` : ''}
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
              {targetBox && (
                <p className="text-xs text-muted-foreground">
                  Saldo actual: <span className="font-medium">{fmt(targetBox.balance, targetBox.currency)}</span>
                </p>
              )}
            </div>

            {/* Monto */}
            <div className="grid gap-1.5">
              <Label>Monto a enviar{sourceBox ? ` (${sourceBox.currency})` : ''} *</Label>
              <MoneyInput
                currency={sourceBox?.currency || 'ARS'}
                value={amount ?? undefined}
                onValueChange={(v) => setAmount(v)}
                placeholder="0,00"
              />
            </div>

            {/* Sección conversión */}
            {isConversion && (
              <>
                <div className="p-3 rounded-md bg-amber-50 border border-amber-200 text-sm text-amber-800 flex items-start gap-2">
                  <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                  <span>
                    Esta operación es <strong>irreversible</strong>. Verificá el tipo de cambio antes de confirmar.
                  </span>
                </div>

                <div className="grid gap-1.5">
                  <Label>Tipo de cambio (1 USD = X ARS) *</Label>
                  <MoneyInput
                    currency="ARS"
                    value={exchangeRate ?? undefined}
                    onValueChange={(v) => setExchangeRate(v)}
                    placeholder="Ej: 1.200,00"
                  />
                  <p className="text-xs text-muted-foreground">
                    Ingresá cuántos pesos equivale 1 dólar hoy.
                  </p>
                </div>

                {convertedAmount !== null && sourceBox && targetBox && (
                  <div className="p-3 rounded-md bg-blue-50 border border-blue-200 text-sm text-blue-800 text-center font-medium">
                    Enviás {fmt(amount!, sourceBox.currency)} → Recibís {fmt(convertedAmount, targetBox.currency)}
                  </div>
                )}
              </>
            )}

            <div className="grid gap-1.5">
              <Label>Descripción</Label>
              <Textarea
                placeholder={isConversion ? 'Ej: Conversión ARS a USD - cotización del día' : 'Ej: Reposición de caja chica'}
                rows={2}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
              />
            </div>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose} disabled={loading}>
              Cancelar
            </Button>
            <Button type="submit" disabled={loading}>
              {loading ? 'Procesando...' : isConversion ? 'Realizar Conversión' : 'Realizar Transferencia'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
